import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createMemo } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { EventPageHeader } from "~/components/EventPageHeader";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import {
  cancelConnection,
  fetchConnections,
  fetchConnectEvents,
  everClaimedTicket,
  fetchProfile,
  respondConnection,
  type ConnectionItem,
} from "~/lib/api";
import { eventDisplayTitle } from "~/lib/events";
import { connectionsQueryKey, profileQueryKey } from "~/lib/routes";

export function ConnectionsPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventId = () => params().eventId ?? "";

  const events = createQuery(() => ({ queryKey: ["events"], queryFn: fetchConnectEvents }));
  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(eventId()),
    queryFn: () => fetchProfile(eventId() || undefined),
  }));
  const connectionsQuery = createQuery(() => ({
    queryKey: connectionsQueryKey(),
    queryFn: () => fetchConnections(),
    enabled: everClaimedTicket(profileQuery.data),
  }));

  const needsTicket = () =>
    profileQuery.isSuccess && !everClaimedTicket(profileQuery.data);

  const selectedEvent = () =>
    events.data?.data.find((e) => e.ticketing_event_id === eventId());

  const eventTitle = () =>
    eventId() ? eventDisplayTitle(selectedEvent(), eventId()) : "All cities";

  const connectOpen = () => selectedEvent()?.connect_open !== false;

  const incoming = createMemo(() =>
    (connectionsQuery.data?.data ?? []).filter(
      (c) => c.direction === "incoming" && c.status === "pending",
    ),
  );
  const outgoing = createMemo(() =>
    (connectionsQuery.data?.data ?? []).filter(
      (c) => c.direction === "outgoing" && c.status === "pending",
    ),
  );
  const accepted = createMemo(() =>
    (connectionsQuery.data?.data ?? []).filter((c) => c.status === "accepted"),
  );

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["connections"] });
  }

  const respondMutation = createMutation(() => ({
    mutationFn: ({ id, action }: { id: string; action: "accept" | "decline" }) =>
      respondConnection(id, action),
    onSuccess: () => invalidate(),
  }));

  const cancelMutation = createMutation(() => ({
    mutationFn: (id: string) => cancelConnection(id),
    onSuccess: () => invalidate(),
  }));

  function openPeer(card: ConnectionItem) {
    if (eventId()) {
      navigate({
        to: "/directory/$eventId/profile/$profileId",
        params: { eventId: eventId(), profileId: card.peer.user_id },
      });
      return;
    }
    navigate({ to: "/profile/$profileId", params: { profileId: card.peer.user_id } });
  }

  return (
    <section class="page page--flush">
      <Show when={needsTicket()}>
        <div style={{ padding: "20px" }}>
          <TicketClaimGate
            eventId={eventId() || undefined}
            cityLabel={eventTitle()}
            connectOpen={eventId() ? connectOpen() : undefined}
          />
        </div>
      </Show>

      <Show when={!needsTicket()}>
      <EventPageHeader eventTitle={eventTitle()} label="Connections" />
      <div class="page-title-block">
        <h1>Your network</h1>
      </div>

      <Show when={connectionsQuery.isLoading}>
        <p class="muted" style={{ padding: "0 20px" }}>
          Loading connections…
        </p>
      </Show>
      <Show when={connectionsQuery.isError}>
        <div class="error-box" style={{ margin: "0 20px" }}>
          {(connectionsQuery.error as Error).message}
        </div>
      </Show>

      <Show when={incoming().length}>
        <h2 class="section-title">Incoming requests</h2>
        <ul class="connection-list">
          <For each={incoming()}>
            {(item) => (
              <li class="connection-row connection-row--incoming">
                <button type="button" class="connection-peer" onClick={() => openPeer(item)}>
                  <Avatar url={item.peer.avatar_url} name={item.peer.display_name} size="sm" />
                  <div>
                    <strong>{item.peer.display_name}</strong>
                    <span class="muted">{item.peer.headline ?? item.peer.company}</span>
                    <Show when={item.request_note}>
                      <p class="connection-note">"{item.request_note}"</p>
                    </Show>
                  </div>
                </button>
                <div class="connection-actions">
                  <button
                    type="button"
                    class="btn btn-primary"
                    disabled={respondMutation.isPending}
                    onClick={() => respondMutation.mutate({ id: item.id, action: "accept" })}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline"
                    disabled={respondMutation.isPending}
                    onClick={() => respondMutation.mutate({ id: item.id, action: "decline" })}
                  >
                    Decline
                  </button>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={outgoing().length}>
        <h2 class="section-title">Pending outgoing</h2>
        <ul class="connection-list">
          <For each={outgoing()}>
            {(item) => (
              <li class="connection-row connection-row--horizontal">
                <button type="button" class="connection-peer" onClick={() => openPeer(item)}>
                  <Avatar url={item.peer.avatar_url} name={item.peer.display_name} size="sm" />
                  <div>
                    <strong>{item.peer.display_name}</strong>
                    <span class="status-chip">Pending</span>
                  </div>
                </button>
                <button
                  type="button"
                  class="btn btn-outline btn-sm"
                  disabled={cancelMutation.isPending}
                  onClick={() => cancelMutation.mutate(item.id)}
                >
                  Cancel
                </button>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={accepted().length}>
        <h2 class="section-title">Connected</h2>
        <ul class="connection-list">
          <For each={accepted()}>
            {(item) => (
              <li class="connection-row connection-row--horizontal">
                <button type="button" class="connection-peer" onClick={() => openPeer(item)}>
                  <Avatar url={item.peer.avatar_url} name={item.peer.display_name} size="sm" />
                  <div>
                    <strong>{item.peer.display_name}</strong>
                    <span class="muted">{item.peer.company}</span>
                    <Show when={item.peer.email}>
                      <span class="muted">{item.peer.email}</span>
                    </Show>
                  </div>
                </button>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show
        when={
          connectionsQuery.data && !incoming().length && !outgoing().length && !accepted().length
        }
      >
        <div class="empty-state" style={{ margin: "16px 20px" }}>
          <p>No connections yet.</p>
          <p class="muted">Browse the directory and send a request to someone you'd like to meet.</p>
          <button
            type="button"
            class="btn btn-primary"
            onClick={() =>
              eventId()
                ? navigate({ to: "/directory/$eventId", params: { eventId: eventId() } })
                : navigate({ to: "/directory" })
            }
          >
            Browse directory
          </button>
        </div>
      </Show>
      </Show>
    </section>
  );
}
